import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/ui/ProtectedRoute";
import Spinner from "./components/ui/Spinner";
import CookieBanner from "./components/legal/CookieBanner";
import { initAnalytics } from "./utils/analytics";
import { parseLocaleFromPath, localizedPath } from "./i18n/routing";

// Lazy-loaded pages
const HomePage            = lazy(() => import("./pages/HomePage"));
const SearchPage          = lazy(() => import("./pages/SearchPage"));
const ListingDetailPage   = lazy(() => import("./pages/ListingDetailPage"));
const BookingPage         = lazy(() => import("./pages/BookingPage"));
const BecomeHostPage      = lazy(() => import("./pages/BecomeHostPage"));
const WishlistPage        = lazy(() => import("./pages/WishlistPage"));
const MessagesPage        = lazy(() => import("./pages/MessagesPage"));
const TripsPage           = lazy(() => import("./pages/TripsPage"));
const ProfilePage         = lazy(() => import("./pages/ProfilePage"));
const HostDashboardPage   = lazy(() => import("./pages/HostDashboardPage"));
const KYCPage             = lazy(() => import("./pages/KYCPage"));
const PublishMachinePage      = lazy(() => import("./pages/PublishMachinePage"));
const PublishFincaPage        = lazy(() => import("./pages/PublishFincaPage"));
const ChoosePublishTypePage   = lazy(() => import("./pages/ChoosePublishTypePage"));
const PlansPage               = lazy(() => import("./pages/PlansPage"));
const ContabilidadePage       = lazy(() => import("./pages/ContabilidadePage"));
const AsesorPage              = lazy(() => import("./pages/AsesorPage"));
const AsesorAceptarPage       = lazy(() => import("./pages/AsesorAceptarPage"));
const AdminPage           = lazy(() => import("./pages/AdminPage"));
const LeaseRegistryPage   = lazy(() => import("./pages/LeaseRegistryPage"));

// Info / footer pages
const AxudaPage           = lazy(() => import("./pages/AxudaPage"));
const ContactoPage        = lazy(() => import("./pages/ContactoPage"));
const SeguridadePage      = lazy(() => import("./pages/SeguridadePage"));
const CancelacionPage     = lazy(() => import("./pages/CancelacionPage"));
const DespoboacionPage    = lazy(() => import("./pages/DespoboacionPage"));
const FacturacionPage     = lazy(() => import("./pages/FacturacionPage"));
const FiscalidadePage     = lazy(() => import("./pages/FiscalidadePage"));
const PublicarMaquinariaPage = lazy(() => import("./pages/PublicarMaquinariaPage"));
const PublicarTerraPage   = lazy(() => import("./pages/PublicarTerraPage"));
const ProteccionPage      = lazy(() => import("./pages/ProteccionPage"));
const SimuladorPage       = lazy(() => import("./pages/SimuladorPage"));
const B2BPage             = lazy(() => import("./pages/B2BPage"));
const TermosPage          = lazy(() => import("./pages/TermosPage"));
const PrivacidadePage     = lazy(() => import("./pages/PrivacidadePage"));
const CumprimentoPage     = lazy(() => import("./pages/CumprimentoPage"));
const SitemapPage         = lazy(() => import("./pages/SitemapPage"));
const AxudasPage          = lazy(() => import("./pages/AxudasPage"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Spinner size={36} />
    </div>
  );
}

// Sub-tree compartido entre raíz (galego) e prefixo /es (español).
// `key` é necesario para que React non queixe de chaves duplicadas
// cando renderizamos esta árbore dúas veces no mesmo <Routes>.
function buildRoutes(key) {
  return (
    <Route key={key} element={<Layout />}>
      {/* Public routes */}
      <Route index               element={<HomePage />} />
      <Route path="buscar"       element={<SearchPage />} />
      <Route path="anuncio/:id"  element={<ListingDetailPage />} />
      <Route path="ser-propietario" element={<BecomeHostPage />} />
      <Route path="plans"            element={<PlansPage />} />
      <Route path="planes"           element={<PlansPage />} />
      <Route path="propietario/contabilidade" element={<ContabilidadePage />} />

      {/* Info / footer pages */}
      <Route path="axuda"                  element={<AxudaPage />} />
      <Route path="contacto"               element={<ContactoPage />} />
      <Route path="seguridade"             element={<SeguridadePage />} />
      <Route path="cancelacion"            element={<CancelacionPage />} />
      <Route path="despoboacion"           element={<DespoboacionPage />} />
      <Route path="facturacion"            element={<FacturacionPage />} />
      <Route path="fiscalidade"            element={<FiscalidadePage />} />
      <Route path="propietario/maquinaria"   element={<PublicarMaquinariaPage />} />
      <Route path="propietario/terra"        element={<PublicarTerraPage />} />
      <Route path="propietario/proteccion"   element={<ProteccionPage />} />
      <Route path="simulador"              element={<SimuladorPage />} />
      <Route path="b2b"                    element={<B2BPage />} />
      <Route path="termos"                 element={<TermosPage />} />
      <Route path="privacidade"            element={<PrivacidadePage />} />
      <Route path="cumprimento"            element={<CumprimentoPage />} />
      <Route path="sitemap"                element={<SitemapPage />} />
      <Route path="axudas"                 element={<AxudasPage />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="reservar/:id"         element={<BookingPage />} />
        <Route path="favoritos"            element={<WishlistPage />} />
        <Route path="mensaxes"             element={<MessagesPage />} />
        <Route path="reservas"             element={<TripsPage />} />
        <Route path="perfil"               element={<ProfilePage />} />
        <Route path="propietario"          element={<HostDashboardPage />} />
        <Route path="verificar"            element={<KYCPage />} />

        {/* Publish — chooser maquinaria/finca */}
        <Route path="publicar"             element={<ChoosePublishTypePage />} />

        {/* Publish & edit machine */}
        <Route path="publicar/maquina"     element={<PublishMachinePage />} />
        <Route path="editar/maquina/:id"   element={<PublishMachinePage />} />

        {/* Publish & edit finca */}
        <Route path="publicar/finca"       element={<PublishFincaPage />} />
        <Route path="editar/finca/:id"     element={<PublishFincaPage />} />

        {/* Tax registry */}
        <Route path="registro-arrendamentos" element={<LeaseRegistryPage />} />

        {/* Asesor fiscal */}
        <Route path="asesor"               element={<AsesorPage />} />
        <Route path="asesor/aceptar"       element={<AsesorAceptarPage />} />

        {/* Admin */}
        <Route path="admin"                element={<AdminPage />} />
      </Route>
    </Route>
  );
}

// Renderiza a árbore de rutas dentro do prefixo /es. Para non duplicar
// JSX, montamos un <Routes> aniñado: react-router xa "consume" /es e
// estes paths son relativos ao parente.
function NestedRoutes() {
  return (
    <Routes>
      {buildRoutes("nested")}
    </Routes>
  );
}

// Sincroniza i18next co locale derivado do path (override sobre cookie).
// Nada visual; solo efecto colateral.
function LocaleSync() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    const { locale } = parseLocaleFromPath(pathname);
    const current = (i18n.language || "gl").split("-")[0];
    if (locale !== current) {
      i18n.changeLanguage(locale);
    }
    // i18n FÓRA do dep array a propósito: a súa referencia cambia con cada
    // changeLanguage e formaría un loop infinito (React error #185). Só
    // precisa re-correr cando muda a URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
}

// Inxecta tags <link rel="alternate" hreflang="..."> dinámicos no <head>
// segundo a URL actual. SEO: declara que existen as 2 versións por idioma
// dunha mesma páxina, e cal é o "x-default".
function HreflangTags() {
  const { pathname } = useLocation();
  useEffect(() => {
    const origin = window.location.origin;
    const { rest } = parseLocaleFromPath(pathname);
    // Constrúe URLs canónicas por idioma. "rest" sempre comeza por "/".
    const urls = {
      gl: origin + rest,
      es: origin + localizedPath(rest, "es"),
    };
    // Borra os anteriores (poden persistir entre navegacións).
    document
      .querySelectorAll('link[rel="alternate"][data-i18n="1"]')
      .forEach((el) => el.remove());
    const head = document.head;
    const make = (hreflang, href) => {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = hreflang;
      l.href = href;
      l.setAttribute("data-i18n", "1");
      head.appendChild(l);
    };
    make("gl", urls.gl);
    make("es", urls.es);
    make("x-default", urls.gl); // galego = idioma por defecto
  }, [pathname]);
  return null;
}

export default function App() {
  // Mantén <html lang="..."> sincronizado co idioma activo. Crawlers e
  // lectores de pantalla leen este atributo para identificar o idioma do
  // contido (importante para SEO + a11y, e require que cambie ao alternar).
  const { i18n } = useTranslation();
  useEffect(() => {
    const root = (i18n.language || "gl").split("-")[0];
    document.documentElement.lang = root;
  }, [i18n.language]);

  // X-0.B · Inxectar Plausible só se o usuario xa aceptou previamente.
  // O banner xestiona mudanzas posteriores via evento `cookie-consent-changed`.
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Suspense fallback={<PageLoader />}>
      <LocaleSync />
      <HreflangTags />
      <CookieBanner />
      <Routes>
        {/* Spanish prefix tree — mesmas rutas baixo /es. */}
        <Route path="/es/*" element={<NestedRoutes treeKey="es" />} />
        {/* Default (galician) tree at root. */}
        {buildRoutes("gl")}
      </Routes>
    </Suspense>
  );
}
